/**
 * The bookings LIST view. CRM-BUILD item 27's other half.
 *
 * ┌─ ⛔ NO DRAG HERE, AND IT IS DELIBERATE RATHER THAN AN OMISSION ─────────────┐
 * │ Amir 2026-09-15, in those words. A list ordered BY STAGE would have to      │
 * │ reorder itself under the cursor to show a drop, and the thing being         │
 * │ expressed - "this booking is now at that stage" - has a control on the row   │
 * │ that says so in words.                                                     │
 * │                                                                            │
 * │ ⚠️ THE ROW'S STAGE CONTROL AND THE BOARD'S DRAG CALL THE SAME ENDPOINT, so  │
 * │ the loss-reason rule cannot apply to one and not the other. The confirm     │
 * │ below is the same `ConfirmDialog` the board raises.                        │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ NUMBERED PAGES, NOT INFINITE SCROLL. Amir's correction of 2026-09-12 to item
 * 27: he wants a footer that can hold things, and infinite scroll forecloses one.
 * The rows-per-page control is ONE SETTING APPLYING TO EVERY LIST VIEW.
 */
import { useMemo } from 'react'
import { moveBooking, type BookingListPage, type BookingListRow } from '../data/bookings-api.js'
import { ROWS_PER_PAGE } from '../../../src/services/list-paging.js'
import { formatMoney } from '../data/money.js'
import { ConfirmDialog } from '../ConfirmDialog.js'
import { useCardMove } from './useCardMove.js'

const COUNT = new Intl.NumberFormat()

type Props = {
  page: BookingListPage | null
  params: URLSearchParams
  setParam: (key: string, value: string | null) => void
  /** ⛔ TWO PARAMETERS IN ONE CHANGE. A column header sets `sort` AND
   *  `direction`, and two `setParam` calls both read the same props snapshot, so
   *  the second discards the first. Found by the browser suite. */
  setParams: (changes: Record<string, string | null>) => void
  onOpenBooking: (reference: number) => void
  onOpenContact: (reference: number) => void
  onChanged: () => void
}

export function BookingList({
  page,
  params,
  setParam,
  setParams,
  onOpenBooking,
  onOpenContact,
  onChanged,
}: Props) {
  const stageLabels = useMemo(
    () => new Map((page?.stages ?? []).map((s) => [s.id, s.label])),
    [page],
  )
  /**
   * ⛔ THE SAME HOOK THE BOARD USES, for the confirm and the announcement. The
   * list never picks a card UP - `carried` stays null - but the loss-reason
   * question, the optimistic state and the roll-back are identical, and a second
   * copy is where the two surfaces would come to disagree about the rule.
   */
  const move = useCardMove({
    stageOrder: (page?.stages ?? []).map((s) => s.id),
    stageLabel: (id) => stageLabels.get(id) ?? 'that stage',
    onChanged,
  })

  if (page === null) return null

  const sort = page.sort
  const direction = page.direction
  const visible = page.columns.filter((c) => c.visible)

  const setSort = (key: string | null) => {
    if (key === null) return
    /** ⚠️ CLICKING THE CURRENT SORT REVERSES IT, which is what a person expects
     *  of a column header and what the contacts list already does. */
    if (key === sort) setParam('direction', direction === 'asc' ? 'desc' : 'asc')
    else setParams({ sort: key, direction: 'asc' })
  }

  return (
    <div className="board__list" data-testid="bookings-list">
      <div className="board__summary">
        {/** ⛔ R-TOTAL: every result set states its total. ⚠️ `null` on a page
         *  past the first means "not recounted", never "none". */}
        <p className="board__total" data-testid="list-total">
          {page.total === null
            ? `Page ${page.page}`
            : `${COUNT.format(page.total)}${page.totalIsCapped ? '+' : ''} ${
                page.total === 1 ? page.entityLabel : page.entityPlural
              }`}
        </p>
        <label className="board__search-label" htmlFor="bookings-search">
          Search
        </label>
        <input
          id="bookings-search"
          className="board__search"
          type="search"
          data-testid="list-search"
          defaultValue={params.get('q') ?? ''}
          placeholder="Reference, title or contact"
          onKeyDown={(e) => {
            if (e.key === 'Enter') setParam('q', (e.target as HTMLInputElement).value)
          }}
          /**
           * ⛔ THE NATIVE × CLEARS THE BOX AND FIRES NO KEY EVENT. Amir's defect
           * 5, 2026-09-16: pressing it looked like it had done something and the
           * list did not change until Enter was pressed on an already-empty
           * field. **Second instance of a control that looks live and is not.**
           *
           * ⚠️ IT REACTS TO EMPTYING ONLY, not to every keystroke. Searching on
           * input would refetch on each letter; CLEARING is the one input event
           * that must act, because the × is the only way to produce it and it
           * has no key to listen for.
           */
          onInput={(e) => {
            if ((e.target as HTMLInputElement).value === '' && (params.get('q') ?? '') !== '') {
              setParam('q', null)
            }
          }}
        />
      </div>

      <p className="board__announce" role="status" aria-live="polite" data-testid="list-announce">
        {move.announcement}
      </p>

      {page.rows.length === 0 ? (
        /** ⛔ THREE ABSENCES, TWO OF THEM REACHABLE HERE. "No bookings at all"
         *  and "none match this view" are different facts and a single blank for
         *  both is the defect this project has met six times. */
        <p className="board__state" data-testid="list-empty">
          {page.emptiness === 'no-bookings-at-all'
            ? `No ${page.entityPlural.toLowerCase()} yet. They appear here once somebody creates one.`
            : `No ${page.entityPlural.toLowerCase()} match this view.`}
        </p>
      ) : (
        <div className="board__table-scroll">
          <table className="board__table">
            <thead>
              <tr>
                {visible.map((col) => (
                  <th key={col.id} scope="col" data-column={col.id}>
                    {col.sort === null ? (
                      col.label
                    ) : (
                      <button
                        type="button"
                        className="board__sort"
                        data-testid={`list-sort-${col.sort}`}
                        aria-sort={
                          sort === col.sort
                            ? direction === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                        onClick={() => setSort(col.sort)}
                      >
                        {col.label}
                        {sort === col.sort && <span aria-hidden="true">{direction === 'asc' ? ' ↑' : ' ↓'}</span>}
                      </button>
                    )}
                  </th>
                ))}
                <th scope="col">Stage Control</th>
              </tr>
            </thead>
            <tbody>
              {page.rows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  page={page}
                  columns={visible}
                  onOpenBooking={onOpenBooking}
                  onOpenContact={onOpenContact}
                  onMove={(stageId) => void moveThrough(row, stageId, move)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/**
       * ⛔ THE FOOTER AMIR ASKED FOR, and it is why numbered pages replaced
       * infinite scroll: *"he wants a footer that can hold things, and infinite
       * scroll forecloses one."*
       */}
      <div className="board__pager" data-testid="list-pager">
        <label htmlFor="bookings-rows">Rows</label>
        <select
          id="bookings-rows"
          data-testid="list-rows"
          value={params.get('limit') ?? '50'}
          onChange={(e) => setParam('limit', e.target.value)}
        >
          {ROWS_PER_PAGE.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={page.page <= 1}
          data-testid="list-prev"
          onClick={() => setParam('page', String(page.page - 1))}
        >
          Previous
        </button>
        <span data-testid="list-page">
          Page {page.page}
          {page.pageCount > 0 && ` of ${page.pageCount}${page.totalIsCapped ? '+' : ''}`}
        </span>
        <button
          type="button"
          disabled={page.pageCount > 0 && page.page >= page.pageCount}
          data-testid="list-next"
          onClick={() => setParam('page', String(page.page + 1))}
        >
          Next
        </button>
      </div>

      {move.confirm !== null && (
        <ConfirmDialog
          title="Reopen This Booking?"
          body={
            move.confirm.lossReason === null
              ? `${move.confirm.title} is recorded as lost. Moving it out of that stage clears that.`
              : `${move.confirm.title} was lost for "${move.confirm.lossReason}". Moving it out of that stage discards that reason, and it is the only record of why.`
          }
          confirmLabel="Reopen and Discard the Reason"
          cancelLabel="Leave It Lost"
          onConfirm={() => move.resolveConfirm(true)}
          onCancel={() => move.resolveConfirm(false)}
        />
      )}
    </div>
  )
}

/** ⚠️ The list's own entry into the shared move: it has no pick-up step, so it
 *  goes straight to the commit the board's drop reaches. */
async function moveThrough(
  row: BookingListRow,
  stageId: string,
  move: ReturnType<typeof useCardMove>,
): Promise<void> {
  move.dropOn(
    {
      bookingId: row.id,
      reference: row.reference,
      title: row.title,
      fromStageId: row.stageId,
      overStageId: stageId,
    },
    stageId,
  )
}

function Row({
  row,
  page,
  columns,
  onOpenBooking,
  onOpenContact,
  onMove,
}: {
  row: BookingListRow
  page: BookingListPage
  columns: BookingListPage['columns']
  onOpenBooking: (reference: number) => void
  onOpenContact: (reference: number) => void
  onMove: (stageId: string) => void
}) {
  return (
    <tr data-testid="list-row" data-booking-reference={row.reference}>
      {columns.map((col) => (
        <td key={col.id} data-column={col.id}>
          <Cell col={col.id} row={row} page={page} onOpenBooking={onOpenBooking} onOpenContact={onOpenContact} />
        </td>
      ))}
      <td>
        {/**
         * ⛔ THE STAGE CONTROL, WHICH IS THE LIST'S ANSWER TO THE DRAG. A select
         * rather than a drop target: the operation is "put this booking at that
         * stage", and a list ordered by stage cannot show it as a movement
         * without reordering itself under the cursor.
         */}
        <select
          className="board__row-stage"
          data-testid="list-row-stage"
          value={row.stageId}
          aria-label={`Stage for ${row.title}`}
          onChange={(e) => onMove(e.target.value)}
        >
          {page.stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </td>
    </tr>
  )
}

function Cell({
  col,
  row,
  page,
  onOpenBooking,
  onOpenContact,
}: {
  col: string
  row: BookingListRow
  page: BookingListPage
  onOpenBooking: (reference: number) => void
  onOpenContact: (reference: number) => void
}) {
  switch (col) {
    case 'fixed:reference':
      return <span className="board__list-ref">{row.reference}</span>
    case 'fixed:contact':
      return row.contactName === null ? (
        <span className="board__absent">No name</span>
      ) : (
        <button type="button" className="board__link" onClick={() => onOpenContact(row.contactReference)}>
          {row.contactName}
        </button>
      )
    case 'fixed:title':
      /** ⛔ THE ROW OPENS THE THREAD, like a card. There is no booking page. */
      return (
        <button type="button" className="board__link" onClick={() => onOpenBooking(row.reference)}>
          {row.title}
        </button>
      )
    case 'fixed:date':
      return row.dateValue === null ? (
        <span className="board__absent">No date set</span>
      ) : (
        <span>{row.dateValue}</span>
      )
    case 'fixed:stage':
      return (
        <span className="board__stage-badge" data-kind={row.stageKind}>
          {row.stageLabel}
        </span>
      )
    case 'fixed:value':
      return row.value === null ? (
        <span className="board__absent">No {page.valueLabel.toLowerCase()} yet</span>
      ) : (
        <span>{formatMoney(row.value, page.currency, page.decimalPlaces)}</span>
      )
    case 'fixed:assigned':
      return row.assignedName === null ? (
        <span className="board__absent">Unassigned</span>
      ) : (
        <span>{row.assignedName}</span>
      )
    case 'fixed:pipeline':
      return <span>{row.pipelineLabel}</span>
    case 'fixed:created':
      return <span>{row.createdAtDisplay}</span>
    case 'fixed:lossReason':
      /** ⚠️ THE WORD, NOT A DASH. A dash in an empty cell says "nothing here" to
       *  a sighted reader and reads as a literal character to a screen reader,
       *  and this column is empty for 96% of rows on every view but Lost. */
      return row.lossReason === null ? (
        <span className="board__absent">Not lost</span>
      ) : (
        <span>{row.lossReason}</span>
      )
    default:
      /** ⚠️ A CUSTOM COLUMN, ALREADY RENDERED TO A STRING BY THE SERVER. The
       *  client has no field-type table and should not grow one. */
      return <span>{row.customValues[col] ?? ''}</span>
  }
}
